import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ScanFace, Download, Moon, Sun } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { blurFaces, blurRegions } from '../../services/tools';
import { fetchBackgroundsAll, saveBackgroundScope } from '../../services/site';

const SCOPE_LABELS = {
  default: 'Toutes les pages', home: 'Accueil', membership: 'Adhésion', contact: 'Contact',
  donate: 'Dons', gallery: 'Galerie', shop: 'Boutique', blog: 'Blog', faq: 'FAQ', carte: 'Carte', equipe: 'Équipe',
};

const BlurTool = () => {
  const { user, token } = useAuth();
  const [file, setFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState('');
  const [strength, setStrength] = useState(14);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null); // { url, faces_detected }
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [bgScope, setBgScope] = useState('default');
  const [manualPoints, setManualPoints] = useState([]);
  const [applyingManual, setApplyingManual] = useState(false);

  if (!user || user.user_type !== 'fondateur') {
    return <div className="p-6 text-sm text-red-500">Accès réservé au fondateur.</div>;
  }

  const onPick = (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    setManualPoints([]);
    setError(''); setInfo('');
    setPreviewSrc(URL.createObjectURL(f));
  };

  const onProcess = async () => {
    if (!file) return;
    try {
      setProcessing(true); setError(''); setInfo(''); setResult(null); setManualPoints([]);
      const res = await blurFaces(file, strength, token);
      setResult(res);
      setInfo(res.faces_detected > 0
        ? `${res.faces_detected} visage(s) détecté(s) et flouté(s). Cliquez sur un visage manqué pour le flouter à la main.`
        : 'Aucun visage détecté automatiquement — cliquez directement sur les visages à flouter.');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setProcessing(false);
    }
  };

  const onResultClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setManualPoints((p) => [...p, { x, y }]);
  };

  const applyManual = async () => {
    if (!result?.url || !manualPoints.length) return;
    try {
      setApplyingManual(true); setError(''); setInfo('');
      const regions = manualPoints.map((p) => ({ x: p.x, y: p.y, w: 0.09, h: 0.09 }));
      const res = await blurRegions(result.url, regions, strength, token);
      setResult({ url: res.url });
      setManualPoints([]);
      setInfo(`${res.regions_applied} zone(s) floutée(s) manuellement.`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setApplyingManual(false);
    }
  };

  const setAsBackground = async (which) => {
    if (!result?.url) return;
    try {
      setError(''); setInfo('');
      const all = await fetchBackgroundsAll(token);
      const existing = (all.scopes && all.scopes[bgScope]) || {};
      const field = which === 'dark' ? 'dark_images' : 'light_images';
      await saveBackgroundScope(bgScope, { ...existing, [field]: [result.url] }, token);
      setInfo(`✅ Image définie comme fond ${which === 'dark' ? 'mode nuit' : 'mode éclairé'} pour « ${SCOPE_LABELS[bgScope] || bgScope} ».`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="blur-tool-page">
      <Button asChild variant="ghost" size="sm"><Link to="/dashboard/fondateur/admin"><ArrowLeft className="h-4 w-4 mr-2" />Centre d'administration</Link></Button>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ScanFace className="h-5 w-5 text-primary" /> Flouter les visages d'une photo</CardTitle>
          <CardDescription>Téléversez une photo : les visages détectés sont anonymisés en micro-pixels. Vous pouvez ensuite la télécharger ou la définir comme fond du site.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {error && <Alert><AlertDescription className="text-red-600" data-testid="blur-error">{error}</AlertDescription></Alert>}
          {info && <Alert><AlertDescription className="text-green-600" data-testid="blur-info">{info}</AlertDescription></Alert>}

          <div className="rounded-lg border border-border p-4 space-y-3">
            <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="blur-file-input" onChange={(e) => onPick(e.target.files?.[0])} className="text-sm w-full" />
            <div className="flex items-center gap-3">
              <label className="text-sm text-muted-foreground">Intensité du floutage</label>
              <input type="range" min="8" max="30" value={strength} data-testid="blur-strength" onChange={(e) => setStrength(Number(e.target.value))} className="flex-1 accent-primary" />
              <span className="text-xs w-8 text-right">{strength}</span>
            </div>
            <Button onClick={onProcess} disabled={!file || processing} data-testid="blur-process-btn">
              {processing ? 'Traitement…' : 'Flouter les visages'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs font-semibold mb-2 text-muted-foreground">Original</p>
              {previewSrc
                ? <img src={previewSrc} alt="original" className="w-full rounded-md object-cover" data-testid="blur-original-preview" />
                : <div className="h-40 grid place-items-center text-xs text-muted-foreground">Aucune image</div>}
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs font-semibold mb-2 text-muted-foreground">Résultat flouté {result?.url && <span className="font-normal">— cliquez sur un visage à flouter</span>}</p>
              {result?.url
                ? (
                  <div className="relative w-full cursor-crosshair" onClick={onResultClick} data-testid="blur-result-clickable">
                    <img src={result.url} alt="flouté" className="w-full rounded-md object-cover select-none pointer-events-none" data-testid="blur-result-preview" />
                    {manualPoints.map((p, i) => (
                      <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full border-2 border-primary bg-primary/30" style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }} />
                    ))}
                  </div>
                )
                : <div className="h-40 grid place-items-center text-xs text-muted-foreground">Le résultat s'affichera ici</div>}
              {result?.url && manualPoints.length > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" onClick={applyManual} disabled={applyingManual} data-testid="blur-apply-manual-btn">{applyingManual ? 'Application…' : `Flouter ${manualPoints.length} zone(s)`}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setManualPoints([])} data-testid="blur-clear-manual-btn">Annuler les points</Button>
                </div>
              )}
            </div>
          </div>

          {result?.url && (
            <div className="flex flex-wrap items-center gap-3 justify-end">
              <select
                data-testid="blur-scope-select"
                value={bgScope}
                onChange={(e) => setBgScope(e.target.value)}
                className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {Object.entries(SCOPE_LABELS).map(([k, v]) => <option key={k} value={k}>Page : {v}</option>)}
              </select>
              <Button asChild variant="outline" data-testid="blur-download-btn"><a href={result.url} download target="_blank" rel="noreferrer"><Download className="h-4 w-4 mr-2" />Télécharger</a></Button>
              <Button variant="outline" onClick={() => setAsBackground('dark')} data-testid="blur-set-dark-btn"><Moon className="h-4 w-4 mr-2" />Fond nuit</Button>
              <Button variant="outline" onClick={() => setAsBackground('light')} data-testid="blur-set-light-btn"><Sun className="h-4 w-4 mr-2" />Fond éclairé</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlurTool;
